// ============================================================
// Moves — Generation and validation
// ============================================================

import { type BoardState, type Position, type Move, type Piece, type Side, type PieceKind, pos, posEq, BOARD_SIZE, NUM_PLANES } from './types';
import { getAt, inBounds, findKing, cloneState, getPieces, computePositionHash } from './board';
import { PIECE_MOVES, pawnCrossDim, knightCrossDim, bishopCrossDim, rookCrossDim, queenCrossDim, kingCrossDim } from './pieces';

// --------------- Helpers ---------------

function opponent(side: Side): Side {
  return side === 'white' ? 'black' : 'white';
}

function forward(side: Side): number {
  return side === 'white' ? 1 : -1;
}

function startRank(side: Side): number {
  return side === 'white' ? 1 : 6;
}

function promoRank(side: Side): number {
  return side === 'white' ? 7 : 0;
}

function makeMove(piece: Piece, from: Position, to: Position, state: BoardState, extra?: Partial<Move>): Move {
  const target = getAt(state, to);
  const isCrossDim = from.w !== to.w;
  return {
    piece,
    from,
    to,
    capture: target && target.side !== piece.side ? target : null,
    promotion: null,
    isCrossDimensional: isCrossDim,
    isCheck: false,      // set later
    isCheckmate: false,  // set later
    castle: null,
    isEnPassant: false,
    notation: '',        // set later
    turn: state.turn,
    ...extra,
  };
}

// --------------- Castling ---------------

function generateCastlingMoves(piece: Piece, from: Position, state: BoardState): Move[] {
  if (piece.kind !== 'king' || piece.moveCount > 0) return [];

  const moves: Move[] = [];
  const side = piece.side;
  const rights = state.castlingRights[side];
  const rank = side === 'white' ? 0 : 7;

  // Must be on home square
  if (from.x !== 4 || from.y !== rank) return [];

  // Kingside
  if (rights.kingSide) {
    const rook = getAt(state, pos(7, rank, from.w));
    if (rook && rook.kind === 'rook' && rook.side === side && rook.moveCount === 0) {
      const clear = !getAt(state, pos(5, rank, from.w)) && !getAt(state, pos(6, rank, from.w));
      if (clear) {
        // King can't be in check, pass through check, or land in check
        const notInCheck = !isInCheck(state, side);
        const passThrough = (() => {
          const mid = cloneState(state);
          mid.grid[from.w][rank][4] = null;
          mid.grid[from.w][rank][5] = piece;
          return !isInCheck(mid, side);
        })();
        const landing = (() => {
          const land = cloneState(state);
          land.grid[from.w][rank][4] = null;
          land.grid[from.w][rank][6] = piece;
          return !isInCheck(land, side);
        })();
        if (notInCheck && passThrough && landing) {
          moves.push(makeMove(piece, from, pos(6, rank, from.w), state, { castle: 'kingside' }));
        }
      }
    }
  }

  // Queenside
  if (rights.queenSide) {
    const rook = getAt(state, pos(0, rank, from.w));
    if (rook && rook.kind === 'rook' && rook.side === side && rook.moveCount === 0) {
      const clear = !getAt(state, pos(1, rank, from.w)) && !getAt(state, pos(2, rank, from.w)) && !getAt(state, pos(3, rank, from.w));
      if (clear) {
        const notInCheck = !isInCheck(state, side);
        const passThrough = (() => {
          const mid = cloneState(state);
          mid.grid[from.w][rank][4] = null;
          mid.grid[from.w][rank][3] = piece;
          return !isInCheck(mid, side);
        })();
        const landing = (() => {
          const land = cloneState(state);
          land.grid[from.w][rank][4] = null;
          land.grid[from.w][rank][2] = piece;
          return !isInCheck(land, side);
        })();
        if (notInCheck && passThrough && landing) {
          moves.push(makeMove(piece, from, pos(2, rank, from.w), state, { castle: 'queenside' }));
        }
      }
    }
  }

  return moves;
}

// --------------- Same-Plane Move Generation ---------------

function generatePawnMoves(piece: Piece, from: Position, state: BoardState): Move[] {
  const moves: Move[] = [];
  const fwd = forward(piece.side);
  const startY = startRank(piece.side);

  // Single advance
  const oneStep = pos(from.x, from.y + fwd, from.w);
  if (inBounds(oneStep) && !getAt(state, oneStep)) {
    if (oneStep.y === promoRank(piece.side)) {
      for (const promo of ['queen', 'rook', 'bishop', 'knight'] as PieceKind[]) {
        moves.push(makeMove(piece, from, oneStep, state, { promotion: promo }));
      }
    } else {
      moves.push(makeMove(piece, from, oneStep, state));
    }

    // Double advance from start
    if (from.y === startY) {
      const twoStep = pos(from.x, from.y + fwd * 2, from.w);
      if (!getAt(state, twoStep)) {
        moves.push(makeMove(piece, from, twoStep, state));
      }
    }
  }

  // Captures (diagonal)
  for (const dx of [-1, 1]) {
    const cap = pos(from.x + dx, from.y + fwd, from.w);
    if (!inBounds(cap)) continue;
    const target = getAt(state, cap);
    if (target && target.side !== piece.side) {
      if (cap.y === promoRank(piece.side)) {
        for (const promo of ['queen', 'rook', 'bishop', 'knight'] as PieceKind[]) {
          moves.push(makeMove(piece, from, cap, state, { promotion: promo }));
        }
      } else {
        moves.push(makeMove(piece, from, cap, state));
      }
    }

    // En passant
    if (state.enPassantTarget && posEq(cap, state.enPassantTarget)) {
      moves.push(makeMove(piece, from, cap, state, { isEnPassant: true }));
    }
  }

  return moves;
}

function generateStandardMoves(piece: Piece, from: Position, state: BoardState): Move[] {
  if (piece.kind === 'pawn') return generatePawnMoves(piece, from, state);

  const moves: Move[] = [];
  const def = PIECE_MOVES[piece.kind];

  for (const vec of def.vectors) {
    let x = from.x + vec.dx;
    let y = from.y + vec.dy;

    while (inBounds(pos(x, y, from.w))) {
      const target = getAt(state, pos(x, y, from.w));
      if (target) {
        if (target.side !== piece.side) {
          moves.push(makeMove(piece, from, pos(x, y, from.w), state));
        }
        break; // blocked
      }
      moves.push(makeMove(piece, from, pos(x, y, from.w), state));
      if (def.style === 'step') break;
      x += vec.dx;
      y += vec.dy;
    }
  }

  return moves;
}

// --------------- Cross-Dimensional Move Generation ---------------

function getCrossDimTargets(piece: Piece, from: Position): Position[] {
  switch (piece.kind) {
    case 'pawn': return pawnCrossDim(from, forward(piece.side));
    case 'knight': return knightCrossDim(from);
    case 'bishop': return bishopCrossDim(from);
    case 'rook': return rookCrossDim(from);
    case 'queen': return queenCrossDim(from);
    case 'king': return piece.crossDimCooldown > 0 ? [] : kingCrossDim(from);
    default: return [];
  }
}

function generateCrossDimMoves(piece: Piece, from: Position, state: BoardState): Move[] {
  const targets = getCrossDimTargets(piece, from);
  const moves: Move[] = [];

  for (const to of targets) {
    if (!inBounds(to)) continue;
    if (posEq(from, to)) continue;

    const target = getAt(state, to);
    if (target && target.side === piece.side) continue; // friendly piece

    // Pawn cross-dim: straight advance can't capture, diagonal can
    if (piece.kind === 'pawn') {
      const isDiagonal = to.x !== from.x;
      if (!isDiagonal && target) continue; // can't capture straight
      if (isDiagonal && !target) continue; // must capture diagonal
    }

    moves.push(makeMove(piece, from, to, state));
  }

  return moves;
}

// --------------- Move Application ---------------

/** Apply a move to a state, returning a new state */
export function applyMove(state: BoardState, move: Move): BoardState {
  const next = cloneState(state);
  const grid = next.grid;

  // Remove piece from source
  grid[move.from.w][move.from.y][move.from.x] = null;

  // Handle en passant capture
  if (move.isEnPassant) {
    const capY = move.from.y; // captured pawn is on same rank as moving pawn
    grid[move.to.w][capY][move.to.x] = null;
  }

  // Place piece at destination
  const movedPiece: Piece = {
    ...move.piece,
    moveCount: move.piece.moveCount + 1,
    lastMovedTurn: state.turn,
    crossDimCooldown: move.piece.kind === 'king' && move.isCrossDimensional ? 2 : 
                      Math.max(0, move.piece.crossDimCooldown - 1),
  };

  // Handle promotion
  const finalPiece: Piece = move.promotion
    ? { ...movedPiece, kind: move.promotion }
    : movedPiece;

  grid[move.to.w][move.to.y][move.to.x] = finalPiece;

  // Handle castling
  if (move.castle) {
    const rank = move.from.y;
    const w = move.from.w;
    if (move.castle === 'kingside') {
      const rook = grid[w][rank][7];
      grid[w][rank][7] = null;
      grid[w][rank][5] = rook;
    } else {
      const rook = grid[w][rank][0];
      grid[w][rank][0] = null;
      grid[w][rank][3] = rook;
    }
  }

  // Update castling rights
  const newCastling = {
    white: { ...next.castlingRights.white },
    black: { ...next.castlingRights.black },
  };
  // King moved
  if (move.piece.kind === 'king') {
    newCastling[move.piece.side].kingSide = false;
    newCastling[move.piece.side].queenSide = false;
  }
  // Rook moved or captured
  if (move.from.w === 0) {
    if (move.from.x === 0 && move.from.y === 0) newCastling.white.queenSide = false;
    if (move.from.x === 7 && move.from.y === 0) newCastling.white.kingSide = false;
    if (move.from.x === 0 && move.from.y === 7) newCastling.black.queenSide = false;
    if (move.from.x === 7 && move.from.y === 7) newCastling.black.kingSide = false;
  }
  if (move.to.w === 0) {
    if (move.to.x === 0 && move.to.y === 0) newCastling.white.queenSide = false;
    if (move.to.x === 7 && move.to.y === 0) newCastling.white.kingSide = false;
    if (move.to.x === 0 && move.to.y === 7) newCastling.black.queenSide = false;
    if (move.to.x === 7 && move.to.y === 7) newCastling.black.kingSide = false;
  }

  // Update en passant target
  let enPassantTarget: Position | null = null;
  if (move.piece.kind === 'pawn' && Math.abs(move.to.y - move.from.y) === 2) {
    enPassantTarget = pos(move.to.x, (move.from.y + move.to.y) / 2, move.from.w);
  }

  // Decrement cross-dim cooldowns for all pieces of the active player
  for (let w = 0; w < NUM_PLANES; w++) {
    for (let y = 0; y < BOARD_SIZE; y++) {
      for (let x = 0; x < BOARD_SIZE; x++) {
        const cell = grid[w][y][x];
        if (cell && cell.side === state.activePlayer && cell.id !== finalPiece.id && cell.crossDimCooldown > 0) {
          grid[w][y][x] = { ...cell, crossDimCooldown: cell.crossDimCooldown - 1 };
        }
      }
    }
  }

  return {
    ...next,
    grid,
    turn: state.turn + 1,
    activePlayer: opponent(state.activePlayer),
    castlingRights: newCastling,
    enPassantTarget,
    halfMoveClock: move.capture || move.piece.kind === 'pawn' ? 0 : state.halfMoveClock + 1,
    hash: computePositionHash({
      ...next,
      grid,
      turn: state.turn + 1,
      activePlayer: opponent(state.activePlayer),
      castlingRights: newCastling,
      enPassantTarget,
      halfMoveClock: move.capture || move.piece.kind === 'pawn' ? 0 : state.halfMoveClock + 1,
      hash: '',
    }),
  };
}

// --------------- Check Detection ---------------

/** Is the given side's king under attack? */
export function isInCheck(state: BoardState, side: Side): boolean {
  const kingPos = findKing(state, side);
  if (!kingPos) return false;

  const opp = opponent(side);
  const enemies = getPieces(state, opp);

  for (const { piece, pos: from } of enemies) {
    // Generate all pseudo-legal moves (without check filtering)
    const standardMoves = generateStandardMoves(piece, from, state);
    const crossDimMoves = generateCrossDimMoves(piece, from, state);
    const allMoves = [...standardMoves, ...crossDimMoves];

    for (const move of allMoves) {
      if (posEq(move.to, kingPos)) return true;
    }
  }

  return false;
}

// --------------- Legal Move Generation ---------------

/** Generate all legal moves for the active player */
export function generateLegalMoves(state: BoardState): Move[] {
  const pieces = getPieces(state, state.activePlayer);
  const legal: Move[] = [];

  for (const { piece, pos: from } of pieces) {
    const standard = generateStandardMoves(piece, from, state);
    const crossDim = generateCrossDimMoves(piece, from, state);
    const castling = generateCastlingMoves(piece, from, state);
    const all = [...standard, ...crossDim, ...castling];

    for (const move of all) {
      // Apply move and check if own king is still safe
      const after = applyMove(state, move);
      if (!isInCheck(after, state.activePlayer)) {
        // Check if this move puts opponent in check
        const givesCheck = isInCheck(after, opponent(state.activePlayer));
        legal.push({ ...move, isCheck: givesCheck });
      }
    }
  }

  return legal;
}
